export interface ScriptTask {
  id: number;
  title: string;
  completed: boolean;
}
// The same rows the Module 2 lessons use: identities with gaps, not list positions.
export const scriptTasks = (): ScriptTask[] => [
  { id: 1, title: 'Prepare the demo', completed: false },
  { id: 4, title: "Read Maya's notes", completed: true },
  { id: 8, title: 'Check the examples', completed: false },
];

export type TransformOperation = 'map' | 'filter' | 'find' | 'sort' | 'copy-sort';
export const transformExpressions: Record<TransformOperation, string> = {
  map: 'const titles = tasks.map((task) => task.title);',
  filter: 'const open = tasks.filter((task) => !task.completed);',
  find: 'const task4 = tasks.find((task) => task.id === 4);',
  sort: 'const sorted = tasks.sort((a, b) => a.title.localeCompare(b.title));',
  'copy-sort': 'const sorted = [...tasks].sort((a, b) => a.title.localeCompare(b.title));',
};
export type TransformResult =
  | { kind: 'titles'; values: string[] }
  | { kind: 'tasks'; values: ScriptTask[] }
  | { kind: 'task'; value: ScriptTask | undefined };
export interface TransformFrame {
  expression: string;
  stored: ScriptTask[];
  result: TransformResult;
  storedChanged: boolean;
  sameArray: boolean;
  message: string;
}
export function applyTransform(operation: TransformOperation): TransformFrame {
  const tasks = scriptTasks();
  const original = scriptTasks();
  const byTitle = (a: ScriptTask, b: ScriptTask) => a.title.localeCompare(b.title);
  let result: TransformResult;
  let sameArray = false;
  switch (operation) {
    case 'map':
      result = { kind: 'titles', values: tasks.map((task) => task.title) };
      break;
    case 'filter':
      result = { kind: 'tasks', values: tasks.filter((task) => !task.completed) };
      break;
    case 'find':
      result = { kind: 'task', value: tasks.find((task) => task.id === 4) };
      break;
    case 'sort': {
      const sorted = tasks.sort(byTitle);
      sameArray = sorted === tasks;
      result = { kind: 'tasks', values: sorted };
      break;
    }
    case 'copy-sort':
      result = { kind: 'tasks', values: [...tasks].sort(byTitle) };
      break;
  }
  const storedChanged = JSON.stringify(tasks) !== JSON.stringify(original);
  const messages: Record<TransformOperation, string> = {
    map: 'map built a new array of 3 titles. The stored array still holds its 3 task records, unchanged.',
    filter:
      'filter returned a new array with the 2 open tasks. Task 4 is still stored; it was left out of the result, not deleted.',
    find: 'find returned the one record whose id is 4. It would return undefined if nothing matched. Nothing was copied or removed.',
    sort: 'sort reordered the stored array in place and returned that same array. Every part of the page that holds tasks now sees the new order, whether it wanted it or not.',
    'copy-sort':
      'The spread copied the array first, so sort reordered the copy. The stored array keeps its original order, and the result is a separate array.',
  };
  return {
    expression: transformExpressions[operation],
    stored: tasks,
    result,
    storedChanged,
    sameArray,
    message: messages[operation],
  };
}

export const projectTaskType = `type ProjectTask = {
  id: number;
  title: string;
  completed: boolean;
  project_id: number;
  assignee_id: number | null;
  assignee_name: string | null;
};`;
export type AssigneeInput = 'number' | 'null' | 'string' | 'undefined';
const assigneeLiterals: Record<AssigneeInput, string> = {
  number: '2',
  null: 'null',
  string: "'2'",
  undefined: 'undefined',
};
export interface TypeCheckFrame {
  literal: string;
  errors: string[];
  accepted: boolean;
  runtime: string;
}
export function typeCheckRecord(assignee: AssigneeInput, misspelt: boolean): TypeCheckFrame {
  const nameField = misspelt ? 'asignee_name' : 'assignee_name';
  const literal = `const task: ProjectTask = {
  id: 8,
  title: 'Check the examples',
  completed: false,
  project_id: 1,
  assignee_id: ${assigneeLiterals[assignee]},
  ${nameField}: 'Ravi',
};`;
  const errors: string[] = [];
  if (assignee === 'string')
    errors.push("error TS2322: Type 'string' is not assignable to type 'number | null'.");
  if (assignee === 'undefined')
    errors.push("error TS2322: Type 'undefined' is not assignable to type 'number | null'.");
  if (misspelt)
    errors.push(
      "error TS2561: Object literal may only specify known properties, but 'asignee_name' does not exist in type 'ProjectTask'. Did you mean to write 'assignee_name'?",
    );
  const runtimeById: Record<AssigneeInput, string> = {
    number:
      'At run time assignee_id is the number 2, so task.assignee_id === 2 is true and the row shows Ravi.',
    null: 'At run time assignee_id is null, so the row shows Unassigned. The type allows null on purpose; there is nothing to catch.',
    string:
      "At run time '2' === 2 is false, so the row would show Unassigned although Ravi is assigned. The browser reports no error.",
    undefined:
      'At run time undefined === null and undefined === 2 are both false, so the row would show neither Unassigned nor Ravi. The browser stays silent.',
  };
  const runtime = misspelt
    ? `${runtimeById[assignee]} With the misspelt field, task.assignee_name is undefined, so the name renders as empty text.`
    : runtimeById[assignee];
  return { literal, errors, accepted: errors.length === 0, runtime };
}

export type NarrowingTask = 'assigned' | 'unassigned';
export type NarrowingCode = 'direct' | 'check' | 'optional';
export const narrowingSnippets: Record<NarrowingCode, string> = {
  direct: 'const length = task.assignee_name.length;',
  check: `let label;
if (task.assignee_name === null) {
  label = 'Unassigned';
} else {
  label = \`\${task.assignee_name} (\${task.assignee_name.length} characters)\`;
}`,
  optional: 'const length = task.assignee_name?.length ?? 0;',
};
export interface NarrowingFrame {
  code: string;
  assigneeName: string | null;
  accepted: boolean;
  verdict: string;
  runtime: string;
  message: string;
}
export function narrowingFrame(task: NarrowingTask, code: NarrowingCode): NarrowingFrame {
  const assigneeName = task === 'assigned' ? 'Ravi' : null;
  const frame = { code: narrowingSnippets[code], assigneeName };
  if (code === 'direct')
    return {
      ...frame,
      accepted: false,
      verdict: "error TS18047: 'task.assignee_name' is possibly 'null'.",
      runtime:
        assigneeName === null
          ? "TypeError: Cannot read properties of null (reading 'length'). The page stops rendering."
          : `length is ${assigneeName.length}`,
      message:
        'The checker rejects this line for every task, because the type says the name may be null. The browser only fails for the unassigned task, and only when that row is rendered.',
    };
  if (code === 'check')
    return {
      ...frame,
      accepted: true,
      verdict:
        'No errors. Inside the else branch the checker knows assignee_name is a string, so .length is allowed there.',
      runtime:
        assigneeName === null
          ? 'label is "Unassigned"'
          : `label is "${assigneeName} (${assigneeName.length} characters)"`,
      message:
        'Comparing with null first narrows the type. Both tasks have a defined result, and the absent case has its own words instead of a crash.',
    };
  return {
    ...frame,
    accepted: true,
    verdict: 'No errors. ?. stops at null and ?? supplies the fallback value 0.',
    runtime: `length is ${assigneeName === null ? 0 : assigneeName.length}`,
    message:
      'Optional chaining is the compact form of the same check. It is right when a fallback value is the correct answer; use the if branch when the absent case needs different words or actions.',
  };
}

export interface PipelineState {
  source: number;
  built: number;
  shown: number;
  watch: boolean;
  message: string;
}
export const initialPipeline = (): PipelineState => ({
  source: 1,
  built: 1,
  shown: 1,
  watch: false,
  message:
    'Source, bundle and the Open app tab all show v1. Edit the source and watch which of the three changes.',
});
export type PipelineAction = 'edit' | 'rebuild' | 'reload' | 'toggle-watch' | 'reset';
export function pipelineTransition(state: PipelineState, action: PipelineAction): PipelineState {
  switch (action) {
    case 'reset':
      return initialPipeline();
    case 'edit': {
      const source = state.source + 1;
      return state.watch
        ? {
            ...state,
            source,
            built: source,
            message: `Saved TaskPage.tsx as v${source}. The watcher rebuilt dist/ to v${source} right away. The Open app tab still runs v${state.shown} until you reload it.`,
          }
        : {
            ...state,
            source,
            message: `Saved TaskPage.tsx as v${source}. dist/ still holds the v${state.built} bundle and the Open app tab still runs v${state.shown}. Nothing in the browser changed.`,
          };
    }
    case 'rebuild':
      return state.built === state.source
        ? {
            ...state,
            message: `dist/ already matches the source (v${state.source}). There was nothing to rebuild.`,
          }
        : {
            ...state,
            built: state.source,
            message: `npm run build compiled the TypeScript and JSX into a plain JavaScript bundle. dist/ is now v${state.source}; the tab still runs the bundle it loaded earlier.`,
          };
    case 'reload':
      return state.shown === state.built
        ? {
            ...state,
            message: `Reloaded. The tab already ran the latest bundle (v${state.built}), so nothing visible changed.`,
          }
        : {
            ...state,
            shown: state.built,
            message: `Reloaded. FastAPI served dist/ again, so the Open app tab now runs the v${state.built} bundle.`,
          };
    case 'toggle-watch':
      return state.watch
        ? {
            ...state,
            watch: false,
            message:
              'Stopped the watcher. Saves no longer rebuild dist/ until you run npm run build yourself.',
          }
        : {
            ...state,
            watch: true,
            message:
              'npm run watch is running: every saved change is rebuilt into dist/ automatically. Reloading the Open app tab is still your step.',
          };
  }
}
export function pipelineStatus(state: PipelineState) {
  return {
    bundleStale: state.built !== state.source,
    tabStale: state.shown !== state.built,
  };
}
